import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { HcSort, HcTableDataSource, ModalOptions, ModalService, PaginationComponent } from '@healthcatalyst/cashmere';
import { AdminStringEditorComponent } from '../string-editor/admin-string-editor.component';
import { TypeService } from 'src/app/shared/services/type.service';
import { ProductLineService } from 'src/app/shared/services/product-line.service';
import { StatusService } from 'src/app/shared/services/status.service';
import { SLAService } from 'src/app/shared/services/sla.service';
import { SuiteElement, SuiteService } from 'src/app/shared/services/suites.service';

/** Admin suites */
@Component({
    selector: 'pd-admin-suites',
    templateUrl: './admin-suites.component.html',
    styleUrls: ['./admin-suites.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminSuitesComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<any> = new HcTableDataSource();
    suiteList: any[] = [];
    loading = true;
    loadError = false;
    adminSuitesDisplayedColumns: string[] = [
        'item',
        'name',
        'type',
        'productLine',
        'status',
        'sla',
        'revisionDate'
    ];
    adminSuitesPageSize: number = 10;
    filterData: string[] = [];
    filterSelections: string[] = [];
    filterType = '';
    typeFilters: string[] = [];
    statusFilters: string[] = [];
    modalityFilters: string[] = [];
    productLineFilters: string[] = [];
    publishStatusFilters: string[] = [];
    typeText = 'View All';
    statusText = 'View All';
    productLineText = 'View All';
    publishStatusText = 'View All';
    typeKey: { [key: string]: string } = {};
    productLineKey: { [key: string]: string } = {};
    statusKey: { [key: string]: string } = {};
    slaKey: { [key: string]: string } = {};

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get adminSuitesListLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor(
        private suiteService: SuiteService,
        private typeService: TypeService,
        private productLineService: ProductLineService,
        private statusService: StatusService,
        private slaService: SLAService,
        private modalService: ModalService
    ) { }

    ngOnInit(): void {
        this.typeService.catTypeKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list && this.typeService.category === 'suite') {
                this.typeKey = list;
                if (this.productLineKey 
                    && this.statusKey 
                    && this.slaKey) {
                    this.loadSuites();
                }
            } else {
                this.typeService.loadCatTypes('suite');
            }
        });

        this.productLineService.productLineKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.productLineKey = list;
                if (this.typeKey 
                    && this.statusKey 
                    && this.slaKey
                    && this.typeService.category === 'suite') {
                    this.loadSuites();
                }
            } else {
                this.productLineService.refresh();
            }
        });

        this.statusService.statusKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.statusKey = list;
                if (this.typeKey 
                    && this.productLineKey 
                    && this.slaKey
                    && this.typeService.category === 'suite') {
                    this.loadSuites();
                }
            } else {
                this.statusService.refresh();
            }
        });

        this.slaService.slaKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.slaKey = list;
                if (this.typeKey 
                    && this.productLineKey 
                    && this.statusKey
                    && this.typeService.category === 'suite') {
                    this.loadSuites();
                }
            } else {
                this.slaService.refresh();
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.initSorting();
    }

    loadSuites(): void {
        this.suiteService.suiteUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.loading = false;
                this.loadError = false;
                this.suiteList = [];

                // Parse the id fields for table content
                list.forEach(element => {
                    let tempItem = {
                        'item': element.item,
                        'name': element.name,
                        'type': '',
                        'typeID':element.typeID,
                        'productLine': '',
                        'productLineID': element.productLineID,
                        'status': '',
                        'statusID': element.statusID,
                        'sla': '',
                        'slaid': element.slaid,
                        'revisionDate': element.revisionDate,
                        'suiteID': element.suiteID,
                        'published': element.published
                    };

                    tempItem['type'] = this.formatID( element['typeID'], 'typeID');
                    tempItem['productLine'] = this.formatID( element['productLineID'], 'productLineID');
                    tempItem['status'] = this.formatID( element['statusID'], 'statusID');
                    tempItem['sla'] = this.formatID( element['slaid'], 'slaid');

                    this.suiteList.push( tempItem );
                });

                this.dataSource = new HcTableDataSource(this.suiteList);
                
                if (this.sort && this.paginator) {
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                    this.initSorting();
                }
            } else {
                this.loadError = this.suiteService.loadFailed;
                if (!this.loadError) {
                    this.suiteService.refresh();
                } else {
                    this.loading = false;
                }
            }
        })
    }

    applySearchFilter(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
    }

    storeSelectedTextValue(filterArray: string[]) {
        switch (this.filterType) {
            case 'type':
                if (filterArray.length == 1) this.typeText = filterArray[0];
                else if (filterArray.length > 1) this.typeText = `${filterArray.length} selected`;
                else this.typeText = 'View All';
                break;
            case 'productLine':
                if (filterArray.length == 1) this.productLineText = filterArray[0];
                else if (filterArray.length > 1) this.productLineText = `${filterArray.length} selected`;
                else this.productLineText = 'View All';
                break;
            case 'status':
                if (filterArray.length == 1) this.statusText = filterArray[0];
                else if (filterArray.length > 1) this.statusText = `${filterArray.length} selected`;
                else this.statusText = 'View All';
                break;
            case 'published':
                if (filterArray.length == 1) this.publishStatusText = filterArray[0];
                else if (filterArray.length > 1) this.publishStatusText = `${filterArray.length} selected`;
                else this.publishStatusText = 'View All';
                break;
            default:
                break;
        }
    }

    storeFilterData(type: string) {
        let filterData: string[] = [];
        this.filterType = type;

        //add clean strings for publish status
        if (type === 'published') {
            filterData.push('Published');
            filterData.push('Unpublished');
        } else {
            for (let i = 0; i < this.suiteList.length; i++) {
                if (this.suiteList[i][type]) {
                    if (!filterData.includes(this.suiteList[i][type])) {
                        filterData.push(this.suiteList[i][type]);
                    }
                }
            }
        }
        this.filterData = filterData;
    }

    filterSuites() {
        let filteredSuites: SuiteElement[] = [];

        for (let i = 0; i < this.suiteList.length; i++) {
            let typeFilterMatch = this.typeFilters.length === 0;
            if (this.typeFilters.includes(this.suiteList[i]['type'])) {
                typeFilterMatch = true;
            }

            let statusFilterMatch = this.statusFilters.length === 0;
            if (this.statusFilters.includes(this.suiteList[i]['status'])) {
                statusFilterMatch = true;
            }

            let productLineFilterMatch = this.productLineFilters.length === 0;
            if (this.productLineFilters.includes(this.suiteList[i]['productLine'])) {
                productLineFilterMatch = true;
            }

            // check string filter values from filter popoover against boolean values from data
            let publishStatusFilterMatch = this.publishStatusFilters.length === 0;
            if (
                (this.publishStatusFilters.includes('Published') && this.suiteList[i]['published'] === true) ||
                (this.publishStatusFilters.includes('Unpublished') && this.suiteList[i]['published'] === false)
            ) {
                publishStatusFilterMatch = true;
            }

            if (typeFilterMatch && statusFilterMatch && productLineFilterMatch && publishStatusFilterMatch) {
                filteredSuites.push(this.suiteList[i]);
            }
        }

        //grab filter that was applied from text
        let textFilter = this.dataSource.filter;

        //set data source to the filtered suite list
        this.dataSource = new HcTableDataSource(filteredSuites);
        // this.dataSource.sort = this.sort;

        //reset paginator and sort
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        //add the text filter
        this.dataSource.filter = textFilter;
    }

    storeFilterArray(filterArray: string[]) {
        this.storeSelectedTextValue(filterArray);

        if (this.filterType === 'type') {
            this.typeFilters = filterArray;
        } else if (this.filterType === 'status') {
            this.statusFilters = filterArray;
        } else if (this.filterType === 'productLine') {
            this.productLineFilters = filterArray;
        } else if (this.filterType === 'published') {
            this.publishStatusFilters = filterArray;
        }

        this.filterSuites();
    }

    //create an object from a string
    createObj(obj: any) {
        return JSON.parse(obj);
    }

    openStringEditor(type: string) {
        const options: ModalOptions = {
            closeIcon: true,
            size: 'md',
            data: {
                type: type,
                category: 'Suite'
            }
        };

        this.modalService.open(AdminStringEditorComponent, options);
    }

    formatID(id: number, type: string) {
        let reformattedID: string = '';

        if (type === "typeID") {
            reformattedID = this.typeKey[id.toString()];
        } else if (type === "productLineID") {
            reformattedID = this.productLineKey[id.toString()];
        } else if (type === 'statusID') {
            reformattedID = this.statusKey[id.toString()];
        } else if (type === 'slaid') {
            reformattedID = this.slaKey[id.toString()];
        }

        return reformattedID
    }

    initSorting(): void {
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {     
                case 'revisionDate': {
                    const sortDate = new Date(item.revisionDate);
                    return sortDate.getTime();
                }
                case 'type':
                case 'productLine':
                case 'status':
                case 'item':
                case 'sla':
                case 'name': {
                    return item[property];
                }
                default: {
                    return '';
                }
            }
        };
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
